/**
 * Phibian Console's own sections. Each renders a SERVED read or says plainly
 * that it is not connected — see `platform/sections.ts`. The two pre-existing
 * areas (Organizations = CustomersArea, Global Knowledge) keep their screens.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ConsoleClient, ConsoleOutcome } from '../platform/client.js';
import type { PlatformActor } from '../platform/credentials.js';
import { describeReady, ready } from '../platform/sections.js';
import type { ConsoleSection, ReadyBody, ReadyReading } from '../platform/sections.js';
import { describeJobsHealth } from '../platform/jobsHealth.js';
import type { JobsHealthBody, JobsHealthReading } from '../platform/jobsHealth.js';
import {
  customerAdoption,
  customerReadiness,
  customerIntegrations,
  customerOnboarding,
  customers,
  customerSummary,
  jobsHealth,
} from '../platform/tenancy.js';
import type {
  CustomerAdoptionRead,
  CustomerIntegrationsRead,
  CustomerDirectory,
  CustomerOnboardingRead,
  CustomerReadinessRead,
  PlatformCustomerSummary,
} from '../platform/tenancy.js';
import { JobsHealthPanel } from './JobsHealthPanel.js';
import { ConnectorHead, ConnectorRows } from './Connectors.js';

type Props = { actor: PlatformActor; client: ConsoleClient };

const asIs = <T,>(o: T): T => o;

export function NotConnected({ section }: { section: ConsoleSection }) {
  return (
    <section>
      <h2>{section.label} — not connected</h2>
      <p>{section.purpose}</p>
      <p className="quiet">
        <strong>Data lives in:</strong> {section.source}
      </p>
      <p className="quiet">
        <strong>Blocked on:</strong> {section.blockedOn}
      </p>
      <p className="quiet">
        Nothing is shown here until that read exists. An empty chart would look like a measured
        zero.
      </p>
    </section>
  );
}

function useRead<T, R>(
  load: () => Promise<ConsoleOutcome<T>>,
  describe: (o: ConsoleOutcome<T>) => R,
): [R | null, () => void] {
  const [value, setValue] = useState<R | null>(null);
  const run = useCallback(() => {
    setValue(null);
    void load().then((o) => setValue(describe(o)));
  }, [load, describe]);
  useEffect(run, [run]);
  return [value, run];
}

function ReadyPanel({ reading }: { reading: ReadyReading | null }) {
  if (reading === null) return <p className="quiet">Reading /ready…</p>;
  if (reading.kind === 'unreadable')
    return (
      <div className="banner" role="alert">
        <strong>The API is not answering ready.</strong> {reading.message}
      </div>
    );
  if (reading.kind === 'malformed')
    return (
      <div className="banner" role="alert">
        <strong>/ready answered without a status.</strong> Not read as healthy.
      </div>
    );
  return (
    <>
      <p>
        <code>GET /ready</code> → <strong>{reading.status}</strong>
        <span className="quiet"> · reported {reading.reportedAt ?? 'without a time'}</span>
      </p>
      <ul>
        {reading.checks.map((c) => (
          <li key={c.name}>
            <code>{c.name}</code> — {c.status}
          </li>
        ))}
      </ul>
    </>
  );
}

function useHealth({ actor, client }: Props) {
  const loadReady = useCallback(() => client<ReadyBody>(ready(), actor), [actor, client]);
  const loadJobs = useCallback(() => client<JobsHealthBody>(jobsHealth(), actor), [actor, client]);
  const [readyReading, rerunReady] = useRead(loadReady, describeReady);
  const [jobs, rerunJobs] = useRead<JobsHealthBody, JobsHealthReading>(
    loadJobs,
    describeJobsHealth,
  );
  return { readyReading, jobs, refresh: () => (rerunReady(), rerunJobs()) };
}

export function SystemHealth(props: Props) {
  const { readyReading, jobs, refresh } = useHealth(props);
  return (
    <>
      <section>
        <h2>API readiness</h2>
        <ReadyPanel reading={readyReading} />
        <button type="button" onClick={refresh}>
          refresh
        </button>
      </section>
      <JobsHealthPanel health={jobs} />
    </>
  );
}

export function Deployments(props: Props) {
  const loadReady = useCallback(
    () => props.client<ReadyBody>(ready(), props.actor),
    [props.actor, props.client],
  );
  const [reading] = useRead(loadReady, describeReady);
  return (
    <section>
      <h2>Running build</h2>
      {reading?.kind === 'read' ? (
        <p>
          <code>{reading.service ?? 'service unnamed'}</code> reports version{' '}
          <strong>{reading.version ?? 'not reported'}</strong>
          {reading.uptimeSeconds === null ? null : (
            <span className="quiet"> · up {reading.uptimeSeconds}s on this instance</span>
          )}
        </p>
      ) : (
        <ReadyPanel reading={reading} />
      )}
      <p className="quiet">
        This is the API&apos;s own report of itself. Deploy history (GitHub Actions runs, Cloud Run
        revisions) is not read here yet; production deploys are dispatched by the release owner
        through <code>deploy-production.yml</code>.
      </p>
    </section>
  );
}

export function Overview(props: Props) {
  const { readyReading, jobs } = useHealth(props);
  const loadDirectory = useCallback(
    () => props.client<CustomerDirectory>(customers(), props.actor),
    [props.actor, props.client],
  );
  const [directory] = useRead(loadDirectory, asIs);
  const loadSummary = useCallback(
    () => props.client<PlatformCustomerSummary>(customerSummary(), props.actor),
    [props.actor, props.client],
  );
  const [summary] = useRead(loadSummary, asIs);

  // Healthy, degraded, failed and UNKNOWN are four different answers; a read
  // that did not complete is never shown as healthy (owner directive §4).
  const readyTone =
    readyReading === null
      ? null
      : readyReading.kind !== 'read'
        ? (['urgent', 'Not answering'] as const)
        : readyReading.status === 'ok'
          ? (['good', 'Ready'] as const)
          : readyReading.status === 'degraded'
            ? (['look', 'Degraded'] as const)
            : (['urgent', readyReading.status] as const);
  const jobsTone =
    jobs === null
      ? null
      : jobs.kind === 'healthy'
        ? (['good', 'Healthy'] as const)
        : jobs.kind === 'paused'
          ? (['look', 'Paused for deploy'] as const)
          : jobs.kind === 'unhealthy'
            ? (['urgent', 'Unhealthy'] as const)
            : (['unsure', jobs.kind === 'unreadable' ? 'Not answering' : 'Unknown'] as const);

  return (
    <>
      <div className="metrics">
        <div className="metric">
          <span className="metric-label">API readiness</span>
          {readyTone ? (
            <span className="pill" data-tone={readyTone[0]}>
              {readyTone[1]}
            </span>
          ) : (
            <span className="quiet">Reading…</span>
          )}
          <span className="metric-note">
            {readyReading?.kind === 'read'
              ? `${readyReading.service ?? 'api'} ${readyReading.version ?? ''}`
              : 'GET /ready'}
          </span>
        </div>
        <div className="metric">
          <span className="metric-label">Durable jobs</span>
          {jobsTone ? (
            <span className="pill" data-tone={jobsTone[0]}>
              {jobsTone[1]}
            </span>
          ) : (
            <span className="quiet">Reading…</span>
          )}
          <span className="metric-note">GET /api/system/jobs/health</span>
        </div>
        <div className="metric">
          <span className="metric-label">Customer organizations</span>
          <span className="metric-value num">
            {directory?.kind === 'ok' ? directory.value.customers.length : '—'}
          </span>
          <span className="metric-note">
            {directory?.kind === 'ok'
              ? `${directory.value.customers.filter((c) => c.status === 'active').length} active`
              : directory
                ? 'Directory did not answer'
                : 'Reading…'}
          </span>
        </div>
      </div>
      <section>
        <h2>Readiness</h2>
        <ReadyPanel reading={readyReading} />
      </section>
      <JobsHealthPanel health={jobs} />
      <section>
        <h2>Customers</h2>
        {directory === null ? (
          <p className="quiet">Reading…</p>
        ) : directory.kind === 'ok' ? (
          <p>
            <strong>{directory.value.customers.length}</strong> organizations in the directory (
            {directory.value.customers.filter((c) => c.status === 'active').length} active).
          </p>
        ) : (
          <div className="banner" role="alert">
            <strong>The directory did not answer.</strong> {directory.message}
          </div>
        )}
        <CustomerSummaryPanel summary={summary} />
      </section>
    </>
  );
}

/**
 * Platform-wide customer summary (Phase C): counts only, across every org, from
 * GET /api/system/customers/summary. Honest states — a read that did not
 * complete is never shown as zeros (owner directive §4): loading, a named
 * refusal/transport error, or the measured counts (a real 0 included).
 */
export function CustomerSummaryPanel({
  summary,
}: {
  summary: ConsoleOutcome<PlatformCustomerSummary> | null;
}) {
  if (summary === null) return <p className="quiet">Reading the customer summary…</p>;
  if (summary.kind !== 'ok')
    return (
      <div className="banner" role="status">
        <strong>Customer summary unavailable.</strong> {summary.message}
      </div>
    );
  const s = summary.value;
  const statusEntries = Object.entries(s.byStatus).sort((a, b) => b[1] - a[1]);
  return (
    <div data-testid="customer-summary">
      <div className="metrics">
        <div className="metric">
          <span className="metric-label">Organizations</span>
          <span className="metric-value num">{s.totalOrganizations}</span>
          <span className="metric-note">
            {statusEntries.map(([k, n]) => `${n} ${k}`).join(' · ') || 'none yet'}
          </span>
        </div>
        <div className="metric">
          <span className="metric-label">Activation</span>
          <span className="metric-value num">{s.activation.activated}</span>
          <span className="metric-note">
            activated · {s.activation.onboarding} onboarding · {s.activation.notStarted} not started
          </span>
        </div>
        <div className="metric">
          <span className="metric-label">Workforce</span>
          <span className="metric-value num">{s.workforce.workersInvitedOrActive}</span>
          <span className="metric-note">
            workers · {s.workforce.supervisorsActive} supervisors ·{' '}
            {s.workforce.orgsWithActiveWorkforce} orgs staffed
          </span>
        </div>
        <div className="metric">
          <span className="metric-label">Needs attention</span>
          <span
            className="metric-value num"
            data-tone={s.exceptions.orgsWithConnectorNeverSynced > 0 ? 'look' : undefined}
          >
            {s.exceptions.orgsWithConnectorNeverSynced}
          </span>
          <span className="metric-note">orgs with a connector that has never synced</span>
        </div>
      </div>
      <p className="quiet">As of {new Date(s.measuredAt).toLocaleString()} (counts only).</p>
    </div>
  );
}

/**
 * Every customer's onboarding on one board: the directory, then one canonical
 * onboarding read per customer.
 * ponytail: N+1 reads, fine for a pilot-sized directory; a batched platform
 * read is the upgrade once the customer count makes this slow.
 */
export function OnboardingBoard({ actor, client }: Props) {
  const [rows, setRows] = useState<
    | null
    | { kind: 'failed'; message: string }
    | {
        kind: 'read';
        rows: {
          id: string;
          name: string;
          outcome: ConsoleOutcome<CustomerOnboardingRead>;
          ready: ConsoleOutcome<CustomerReadinessRead>;
        }[];
      }
  >(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      const dir = await client<CustomerDirectory>(customers(), actor);
      if (dir.kind !== 'ok') {
        if (live) setRows({ kind: 'failed', message: dir.message });
        return;
      }
      const read = await Promise.all(
        dir.value.customers.map(async (c) => ({
          id: c.id,
          name: c.name,
          outcome: await client<CustomerOnboardingRead>(customerOnboarding(c.id), actor),
          ready: await client<CustomerReadinessRead>(customerReadiness(c.id), actor),
        })),
      );
      if (live) setRows({ kind: 'read', rows: read });
    })();
    return () => {
      live = false;
    };
  }, [actor, client]);

  if (rows === null) return <p className="quiet">Reading the directory…</p>;
  if (rows.kind === 'failed')
    return (
      <div className="banner" role="alert">
        <strong>The directory did not answer.</strong> {rows.message}
      </div>
    );
  return (
    <section>
      <h2>Onboarding — {rows.rows.length} organizations</h2>
      <table className="stack">
        <thead>
          <tr>
            <th>Organization</th>
            <th>Progress</th>
            <th>Current step</th>
            <th>Administrator</th>
            <th>Sites · programs</th>
            <th>EVV mappings</th>
            <th>Live</th>
          </tr>
        </thead>
        <tbody>
          {rows.rows.map(({ id, name, outcome, ready }) =>
            outcome.kind === 'ok' ? (
              <tr key={id}>
                <td data-label="Organization">{name}</td>
                <td data-label="Progress">
                  {outcome.value.onboarding.completedSteps.length}
                  {outcome.value.onboarding.totalSteps === undefined
                    ? ` complete · ${outcome.value.onboarding.percentComplete}%`
                    : ` / ${outcome.value.onboarding.totalSteps}`}
                </td>
                <td data-label="Current step">
                  <code>{outcome.value.onboarding.currentStep}</code>
                </td>
                {ready.kind === 'ok' ? (
                  <>
                    <td data-label="Administrator">
                      {ready.value.readiness.firstAdmin.activeAdmins > 0
                        ? `${ready.value.readiness.firstAdmin.activeAdmins} active`
                        : ready.value.readiness.firstAdmin.invitation.replace(/_/g, ' ')}
                    </td>
                    <td data-label="Sites · programs">
                      {ready.value.readiness.sites.active} · {ready.value.readiness.programs.active}
                    </td>
                    <td data-label="EVV mappings">
                      {ready.value.readiness.evv.serviceMappingsInEffect}
                    </td>
                  </>
                ) : (
                  <td data-label="Readiness" colSpan={3} className="quiet">
                    not read — {ready.message}
                  </td>
                )}
                <td data-label="Live">{outcome.value.onboarding.isLive ? 'yes' : 'no'}</td>
              </tr>
            ) : (
              <tr key={id}>
                <td data-label="Organization">{name}</td>
                <td data-label="Status" colSpan={6} className="quiet">
                  not read — {outcome.message}
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>
    </section>
  );
}

/**
 * Adoption counts for every customer: the directory, then one counts-only read
 * per customer. ponytail: N+1 reads like the onboarding board; batch when the
 * directory outgrows it.
 */
export function AdoptionBoard({ actor, client }: Props) {
  const [rows, setRows] = useState<
    | null
    | { kind: 'failed'; message: string }
    | {
        kind: 'read';
        rows: { id: string; name: string; outcome: ConsoleOutcome<CustomerAdoptionRead> }[];
      }
  >(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      const dir = await client<CustomerDirectory>(customers(), actor);
      if (dir.kind !== 'ok') {
        if (live) setRows({ kind: 'failed', message: dir.message });
        return;
      }
      const read = await Promise.all(
        dir.value.customers.map(async (c) => ({
          id: c.id,
          name: c.name,
          outcome: await client<CustomerAdoptionRead>(customerAdoption(c.id), actor),
        })),
      );
      if (live) setRows({ kind: 'read', rows: read });
    })();
    return () => {
      live = false;
    };
  }, [actor, client]);

  if (rows === null) return <p className="quiet">Reading the directory…</p>;
  if (rows.kind === 'failed')
    return (
      <div className="banner" role="alert">
        <strong>The directory did not answer.</strong> {rows.message}
      </div>
    );
  const pair = (c: { active: number; invited: number }) =>
    `${c.active} active · ${c.invited} invited`;
  return (
    <section>
      <h2>Workers and adoption — {rows.rows.length} organizations</h2>
      <p className="quiet">
        Counts only. A person holding two of these roles is counted under each. Sign-in recency is
        not shown: <code>app_user.last_login_at</code> is never written.
      </p>
      <table className="stack">
        <thead>
          <tr>
            <th>Organization</th>
            <th>Administrators</th>
            <th>Supervisors</th>
            <th>Workers</th>
            <th>Invitations pending</th>
            <th>…past expiry</th>
          </tr>
        </thead>
        <tbody>
          {rows.rows.map(({ id, name, outcome }) =>
            outcome.kind === 'ok' ? (
              <tr key={id}>
                <td data-label="Organization">{name}</td>
                <td data-label="Administrators">{pair(outcome.value.adoption.people.org_admin)}</td>
                <td data-label="Supervisors">{pair(outcome.value.adoption.people.supervisor)}</td>
                <td data-label="Workers">{pair(outcome.value.adoption.people.worker)}</td>
                <td data-label="Invitations pending">
                  {outcome.value.adoption.invitations.pending}
                </td>
                <td data-label="Past expiry">
                  {outcome.value.adoption.invitations.pendingPastExpiry}
                </td>
              </tr>
            ) : (
              <tr key={id}>
                <td data-label="Organization">{name}</td>
                <td data-label="Status" colSpan={5} className="quiet">
                  not read — {outcome.message}
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>
    </section>
  );
}

/**
 * Every customer's connectors on one board: the directory, then one
 * integrations read per customer. Attention-first: a connector that needs
 * someone sorts to the top.
 * ponytail: N+1 reads like the onboarding and adoption boards; batch when the
 * directory outgrows it.
 */
export function IntegrationsBoard({ actor, client }: Props) {
  const [rows, setRows] = useState<
    | null
    | { kind: 'failed'; message: string }
    | {
        kind: 'read';
        rows: { id: string; name: string; outcome: ConsoleOutcome<CustomerIntegrationsRead> }[];
      }
  >(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      const dir = await client<CustomerDirectory>(customers(), actor);
      if (dir.kind !== 'ok') {
        if (live) setRows({ kind: 'failed', message: dir.message });
        return;
      }
      const read = await Promise.all(
        dir.value.customers.map(async (c) => ({
          id: c.id,
          name: c.name,
          outcome: await client<CustomerIntegrationsRead>(customerIntegrations(c.id), actor),
        })),
      );
      if (live) setRows({ kind: 'read', rows: read });
    })();
    return () => {
      live = false;
    };
  }, [actor, client]);

  if (rows === null) return <p className="quiet">Reading the directory…</p>;
  if (rows.kind === 'failed')
    return (
      <div className="banner" role="alert">
        <strong>The directory did not answer.</strong> {rows.message}
      </div>
    );

  const flat = rows.rows.flatMap((r) =>
    r.outcome.kind === 'ok' ? r.outcome.value.connectors.map((c) => ({ name: r.name, c })) : [],
  );
  flat.sort((a, b) => Number(b.c.needsAttention) - Number(a.c.needsAttention));
  const unread = rows.rows.filter((r) => r.outcome.kind !== 'ok');
  const withNone = rows.rows.filter(
    (r) => r.outcome.kind === 'ok' && r.outcome.value.connectors.length === 0,
  );

  return (
    <section>
      <h2>
        Integrations — {flat.length} connectors across {rows.rows.length} organizations
        {flat.some((x) => x.c.needsAttention)
          ? ` · ${flat.filter((x) => x.c.needsAttention).length} need attention`
          : ''}
      </h2>
      <p className="quiet">
        Each row is the customer&apos;s own Integrations status, plus failed runs by error code and
        quarantined records over the last 7 days. No connector configuration or record leaves the
        customer.
      </p>
      {flat.length ? (
        <table className="stack">
          <ConnectorHead withCustomer />
          <tbody>
            {flat.map(({ name, c }) => (
              <ConnectorRows key={`${name}${c.kind}`} customer={name} connectors={[c]} />
            ))}
          </tbody>
        </table>
      ) : null}
      {withNone.length ? (
        <p className="quiet">No connector set up: {withNone.map((r) => r.name).join(', ')}.</p>
      ) : null}
      {unread.length ? (
        <div className="banner" role="alert">
          <strong>Not read:</strong>{' '}
          {unread
            .map((r) => `${r.name} (${r.outcome.kind === 'ok' ? '' : r.outcome.message})`)
            .join('; ')}
        </div>
      ) : null}
    </section>
  );
}
